"use client";

import { LearnButton } from "@/components/learn-button";
import { PageHeader } from "@/components/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { DeleteIcon } from "@/components/ui/delete";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SquarePenIcon } from "@/components/ui/square-pen";
import { supabase } from "@/lib/supabase";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { MessageResponse } from "@/components/ai-elements/message";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

type MemoriesPanelProps = {
  currentUserId: string | null;
  onBack?: () => void;
  onLearn?: () => void;
};

type MemoryApiRecord = {
  id: string;
  text: string;
  createdAt: string | null;
  path?: string | null;
  metadata: Record<string, string | null> | null;
  source: string | null;
};

type MemoriesPageResponse = {
  data?: MemoryApiRecord[];
  page?: number;
  pageSize?: number;
  total?: number | null;
  hasMore?: boolean;
};

const SERVER_PAGE_SIZE = 50;

// The /api/memories route derives the acting user from the bearer token — never
// from a userId param — so every request must carry the current access token.
const authHeaders = async (): Promise<Record<string, string>> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session?.access_token ?? ""}`,
  };
};

const getErrorMessage = async (response: Response, fallback: string) => {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
  } catch {
    // ignore
  }
  return fallback;
};

const formatTimestamp = (value: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

export function MemoriesPanel({ currentUserId, onBack, onLearn }: MemoriesPanelProps) {
  const searchParams = useSearchParams();
  const targetMemoryPath = searchParams.get("memoryPath")?.trim() || null;
  const [memories, setMemories] = useState<MemoryApiRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingMemoryId, setDeletingMemoryId] = useState<string | null>(null);
  const [editingMemory, setEditingMemory] = useState<MemoryApiRecord | null>(null);
  const [editText, setEditText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const loadMemories = useCallback(async (targetPage: number = 1) => {
    if (!currentUserId) {
      setMemories([]);
      setPage(1);
      setTotalCount(0);
      setHasMore(false);
      setError("Please sign in to manage memories.");
      setIsLoading(false);
      return;
    }

    const normalizedPage = Math.max(1, Math.floor(targetPage));
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/memories?${new URLSearchParams({
          page: String(normalizedPage),
          pageSize: String(targetMemoryPath ? 200 : SERVER_PAGE_SIZE),
          ...(targetMemoryPath ? { path: targetMemoryPath } : {}),
        }).toString()}`,
        { method: "GET", cache: "no-store", headers: await authHeaders() }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to fetch memories."));
      }

      const payload = (await response.json()) as MemoriesPageResponse;
      setMemories(payload.data ?? []);
      setPage(payload.page ?? normalizedPage);
      setTotalCount(typeof payload.total === "number" ? payload.total : null);
      setHasMore(payload.hasMore === true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Failed to fetch memories.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, targetMemoryPath]);

  useEffect(() => {
    void loadMemories(1);
  }, [loadMemories]);

  const handleDeleteMemory = useCallback(
    async (memoryId: string) => {
      if (!currentUserId) {
        toast.error("Please sign in to delete memories.");
        return;
      }

      const confirmed = window.confirm("Delete this memory from Mem0? This cannot be undone.");
      if (!confirmed) return;

      setDeletingMemoryId(memoryId);

      try {
        const response = await fetch("/api/memories", {
          method: "DELETE",
          headers: await authHeaders(),
          body: JSON.stringify({ memoryId }),
        });

        if (!response.ok) {
          throw new Error(await getErrorMessage(response, "Failed to delete memory."));
        }

        const nextSize = memories.length - 1;
        const targetPage = nextSize === 0 && page > 1 ? page - 1 : page;
        await loadMemories(targetPage);
        toast.success("Memory deleted.");
      } catch (deleteError) {
        toast.error(deleteError instanceof Error ? deleteError.message : "Failed to delete memory.");
      } finally {
        setDeletingMemoryId(null);
      }
    },
    [currentUserId, loadMemories, memories.length, page]
  );

  const handleClearAll = useCallback(async () => {
    if (!currentUserId) return;

    setIsClearingAll(true);

    try {
      const response = await fetch("/api/memories", {
        method: "DELETE",
        headers: await authHeaders(),
        body: JSON.stringify({ clearAll: true }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to clear memories."));
      }

      setMemories([]);
      setPage(1);
      setTotalCount(0);
      setHasMore(false);
      toast.success("All memories cleared.");
    } catch (clearError) {
      toast.error(clearError instanceof Error ? clearError.message : "Failed to clear memories.");
    } finally {
      setIsClearingAll(false);
    }
  }, [currentUserId]);

  const handleEditOpen = useCallback((memory: MemoryApiRecord) => {
    setEditingMemory(memory);
    setEditText(memory.text);
  }, []);

  const handleEditClose = useCallback(() => {
    if (isSaving) return;
    setEditingMemory(null);
    setEditText("");
  }, [isSaving]);

  const handleEditSave = useCallback(async () => {
    if (!currentUserId || !editingMemory) return;

    const trimmed = editText.trim();
    if (!trimmed) return;

    setIsSaving(true);

    try {
      const response = await fetch("/api/memories", {
        method: "PATCH",
        headers: await authHeaders(),
        body: JSON.stringify({ memoryId: editingMemory.id, text: trimmed }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, "Failed to update memory."));
      }

      setMemories((current) =>
        current.map((m) => (m.id === editingMemory.id ? { ...m, text: trimmed } : m))
      );
      setEditingMemory(null);
      setEditText("");
      toast.success("Memory updated.");
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : "Failed to update memory.");
    } finally {
      setIsSaving(false);
    }
  }, [currentUserId, editingMemory, editText]);

  const columns: DataTableColumn<MemoryApiRecord>[] = [
    {
      header: "Memory",
      render: (memory) => (
        <div className="prose prose-sm dark:prose-invert max-w-none text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <MessageResponse isAnimating={false} disallowedElements={["script", "style"]}>
            {memory.text}
          </MessageResponse>
        </div>
      ),
    },
    {
      header: "Created",
      width: "w-[160px]",
      render: (memory) => (
        <span className="text-muted-foreground">{formatTimestamp(memory.createdAt)}</span>
      ),
    },
    {
      header: "Project",
      width: "w-[160px]",
      render: (memory) => (
        <span className="text-muted-foreground">{memory.metadata?.project ?? "—"}</span>
      ),
    },
    {
      header: "Woods Project",
      width: "w-[160px]",
      render: (memory) => (
        <span className="text-muted-foreground">{memory.metadata?.woods_project ?? "—"}</span>
      ),
    },
    {
      header: "Source",
      width: "w-[120px]",
      render: (memory) => (
        <span className="text-muted-foreground">{memory.source ?? "—"}</span>
      ),
    },
    {
      header: "Actions",
      width: "w-[90px]",
      align: "right",
      render: (memory) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(e) => { e.stopPropagation(); handleEditOpen(memory); }}
            disabled={deletingMemoryId === memory.id}
            aria-label="Edit memory"
            title="Edit memory"
          >
            <SquarePenIcon size={16} />
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground transition-colors hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(e) => { e.stopPropagation(); void handleDeleteMemory(memory.id); }}
            disabled={deletingMemoryId === memory.id}
            aria-label="Delete memory"
            title="Delete memory"
          >
            <DeleteIcon size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Memories"
        description="Review, edit, and remove memories tied to your account."
        onBack={onBack}
        actions={
          <div className="flex items-center gap-1.5">
            {onLearn && (
              <LearnButton label="Learn about memories" onClick={onLearn} />
            )}
            {memories.length > 0 && !isLoading && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isClearingAll}>
                  {isClearingAll ? "Clearing…" : "Clear All"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all memories?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {totalCount ?? memories.length}{" "}
                    {(totalCount ?? memories.length) === 1 ? "memory" : "memories"} from your account. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => void handleClearAll()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto w-full max-w-6xl">
          {error ? (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>Unable to load memories</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DataTable
            columns={columns}
            data={memories}
            getRowKey={(m) => m.id}
            loading={isLoading}
            onRefresh={() => void loadMemories(page)}
            searchFilter={(m, q) =>
              (m.text ?? "").toLowerCase().includes(q) ||
              (m.metadata?.project ?? "").toLowerCase().includes(q) ||
              (m.metadata?.woods_project ?? "").toLowerCase().includes(q) ||
              (m.source ?? "").toLowerCase().includes(q)
            }
            searchPlaceholder="Search memory text, path, or source…"
            emptyMessage="No memories captured yet."
            itemLabel="memory"
            pageSize={Math.max(memories.length, 1)}
            selectedKeys={
              targetMemoryPath
                ? new Set(
                    memories
                      .filter((memory) => memory.path === targetMemoryPath)
                      .map((memory) => memory.id)
                  )
                : undefined
            }
          />

          {!isLoading && !error && (memories.length > 0 || page > 1 || hasMore) ? (
            <div className="mt-3 flex items-center justify-between rounded-md border border-border px-3 py-2">
              <p className="text-xs text-muted-foreground">
                Page {page}
                {totalCount !== null ? ` • ${totalCount} total memories` : ""}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => void loadMemories(page - 1)}
                  disabled={page <= 1 || isLoading}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => void loadMemories(page + 1)}
                  disabled={!hasMore || isLoading}
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="size-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={editingMemory !== null} onOpenChange={(open) => { if (!open) handleEditClose(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit memory</DialogTitle>
          </DialogHeader>
          <textarea
            className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 resize-y"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void handleEditSave();
            }}
            disabled={isSaving}
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={handleEditClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={() => void handleEditSave()} disabled={isSaving || !editText.trim()}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
